import { useState } from 'react';
import { apiPost, ApiError } from '../../lib/api';
import { ToolGate } from '../../components/ToolGate';
import { Button, Field, Input, Alert, Badge, Spinner, EmptyState } from '../../components/ui';

interface Stop {
  name: string;
  lat: number;
  lng: number;
  category: string;
  address: string;
  phone: string;
  website: string;
}

interface NearbyResponse {
  ok: boolean;
  count: number;
  route: Stop[];
  error?: string;
}

interface VisitResponse {
  ok: boolean;
  contactId: number;
}

const OUTCOMES = ['Spoke to owner', 'Gatekeeper', 'Not there', 'Interested'] as const;
type Outcome = (typeof OUTCOMES)[number];

export default function WalkIn() {
  const [lat, setLat] = useState<string>('');
  const [lng, setLng] = useState<string>('');
  const [radiusM, setRadiusM] = useState<string>('1500');
  const [geoLoading, setGeoLoading] = useState<boolean>(false);
  const [searching, setSearching] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [route, setRoute] = useState<Stop[] | null>(null);

  // Which card has the outcome picker open
  const [activeOutcomeIdx, setActiveOutcomeIdx] = useState<number | null>(null);
  // Set of visited stop indexes
  const [visited, setVisited] = useState<Set<number>>(new Set());
  // Per-stop logging state
  const [loggingIdx, setLoggingIdx] = useState<number | null>(null);
  const [logError, setLogError] = useState<string | null>(null);

  function handleUseLocation() {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser.');
      return;
    }
    setGeoLoading(true);
    setError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLat(String(pos.coords.latitude));
        setLng(String(pos.coords.longitude));
        setGeoLoading(false);
      },
      (err) => {
        setError(`Location error: ${err.message}`);
        setGeoLoading(false);
      },
    );
  }

  async function handleFindNearby() {
    const latNum = parseFloat(lat);
    const lngNum = parseFloat(lng);
    if (isNaN(latNum) || isNaN(lngNum)) {
      setError('Please provide valid latitude and longitude.');
      return;
    }
    setError(null);
    setSearching(true);
    setRoute(null);
    setVisited(new Set());
    setActiveOutcomeIdx(null);
    try {
      const body: { lat: number; lng: number; radiusM?: number } = { lat: latNum, lng: lngNum };
      const r = parseInt(radiusM, 10);
      if (!isNaN(r) && r > 0) body.radiusM = r;
      const data = await apiPost<NearbyResponse>('/api/tools/walkin/nearby', body);
      if (!data.ok && data.error) {
        setError(data.error);
      } else {
        setRoute(data.route);
      }
    } catch (e) {
      if (e instanceof ApiError) {
        setError(e.message);
      } else {
        setError('An unexpected error occurred.');
      }
    } finally {
      setSearching(false);
    }
  }

  async function handleLogVisit(idx: number, outcome: Outcome) {
    if (!route) return;
    const stop = route[idx];
    setLoggingIdx(idx);
    setLogError(null);
    try {
      await apiPost<VisitResponse>('/api/tools/walkin/visit', {
        businessName: stop.name,
        address: stop.address || undefined,
        lat: stop.lat,
        lng: stop.lng,
        outcome,
      });
      setVisited((prev) => new Set([...prev, idx]));
      setActiveOutcomeIdx(null);
    } catch (e) {
      if (e instanceof ApiError) {
        setLogError(e.message);
      } else {
        setLogError('Failed to log visit.');
      }
    } finally {
      setLoggingIdx(null);
    }
  }

  function toggleOutcomePicker(idx: number) {
    setActiveOutcomeIdx((prev) => (prev === idx ? null : idx));
    setLogError(null);
  }

  return (
    <ToolGate tool="walkin" title="Walk-In">
      <div className="mx-auto max-w-2xl space-y-6 px-4 py-8">
        {/* Heading */}
        <div>
          <h1 className="h-display text-3xl text-soft">Walk-In</h1>
          <p className="mt-1 text-sm text-muted">
            Build a route of nearby businesses and log visits as you go.
          </p>
        </div>

        {/* Location controls */}
        <div className="card space-y-4 p-5">
          <div className="flex flex-wrap items-end gap-3">
            <Button
              variant="ghost"
              onClick={handleUseLocation}
              disabled={geoLoading}
              className="shrink-0"
            >
              {geoLoading ? <Spinner className="h-4 w-4" /> : null}
              Use my location
            </Button>
            <Field label="Latitude">
              <Input
                type="number"
                placeholder="e.g. 40.7128"
                value={lat}
                onChange={(e) => setLat(e.target.value)}
                className="w-36"
              />
            </Field>
            <Field label="Longitude">
              <Input
                type="number"
                placeholder="e.g. -74.0060"
                value={lng}
                onChange={(e) => setLng(e.target.value)}
                className="w-36"
              />
            </Field>
            <Field label="Radius (meters)">
              <Input
                type="number"
                placeholder="1500"
                value={radiusM}
                onChange={(e) => setRadiusM(e.target.value)}
                className="w-28"
              />
            </Field>
          </div>

          <Button onClick={handleFindNearby} disabled={searching || geoLoading} className="w-full sm:w-auto">
            {searching ? <Spinner className="h-4 w-4" /> : null}
            Find nearby
          </Button>
        </div>

        {/* Global error */}
        {error && <Alert kind="error">{error}</Alert>}

        {/* Searching state */}
        {searching && (
          <div className="flex items-center gap-3 py-10 text-muted">
            <Spinner />
            <span className="text-sm">Searching the map…</span>
          </div>
        )}

        {/* Empty state before any search */}
        {!searching && route === null && !error && (
          <EmptyState
            title="Plan your route"
            body="Find businesses near you and we'll order them into an efficient walking route."
          />
        )}

        {/* Route list */}
        {!searching && route !== null && route.length === 0 && (
          <Alert kind="info">No businesses found in that area. Try increasing the radius.</Alert>
        )}

        {!searching && route !== null && route.length > 0 && (
          <ol className="space-y-3">
            {route.map((stop, idx) => {
              const isVisited = visited.has(idx);
              const isActive = activeOutcomeIdx === idx;
              const isLogging = loggingIdx === idx;
              return (
                <li key={idx} className="card p-4">
                  <div className="flex items-start gap-3">
                    {/* Index badge */}
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-line bg-surface-3 text-xs font-bold text-electric">
                      {idx + 1}
                    </span>

                    <div className="min-w-0 flex-1 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-head font-semibold text-soft">{stop.name}</span>
                        {stop.category && (
                          <Badge className="border-electric/40 bg-electric/10 text-electric">
                            {stop.category}
                          </Badge>
                        )}
                        {isVisited && (
                          <Badge className="border-success/40 bg-success/10 text-success">
                            ✓ visited
                          </Badge>
                        )}
                      </div>
                      {stop.address && (
                        <p className="text-sm text-muted">{stop.address}</p>
                      )}

                      <div className="flex flex-wrap items-center gap-3 pt-1">
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${stop.lat},${stop.lng}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-electric underline-offset-2 hover:underline"
                        >
                          Open in Maps
                        </a>
                        {!isVisited && (
                          <Button
                            variant="ghost"
                            className="text-sm"
                            onClick={() => toggleOutcomePicker(idx)}
                            disabled={isLogging}
                          >
                            Log visit
                          </Button>
                        )}
                      </div>

                      {/* Inline outcome picker */}
                      {isActive && !isVisited && (
                        <div className="mt-2 space-y-2">
                          {logError && <Alert kind="error">{logError}</Alert>}
                          <p className="text-xs font-semibold uppercase tracking-wide text-muted">
                            What happened?
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {OUTCOMES.map((o) => (
                              <Button
                                key={o}
                                variant="ghost"
                                className="text-sm"
                                disabled={isLogging}
                                loading={isLogging}
                                onClick={() => handleLogVisit(idx, o)}
                              >
                                {o}
                              </Button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </ToolGate>
  );
}
